import { alpha, type PaletteMode, type ThemeOptions } from '@mui/material'
import type {} from '@mui/x-data-grid-premium/themeAugmentation'
import type {} from '@mui/x-date-pickers-pro/themeAugmentation'

// Design language follows edge226-ai/work (tokens.css), with an emerald accent.
export const tokens = {
	light: {
		page: '#F6F8F7',
		card: '#FFFFFF',
		sidebar: '#EFF3F1',
		ink: '#1A1F1D',
		muted: '#5E6863',
		border: '#DDE3E0',
		input: '#C5CDC9',
		primary: '#059669',
		primaryHover: '#047857',
		onPrimary: '#FFFFFF',
		activeBg: '#ECFDF5',
		activeFg: '#065F46',
		gridHeader: '#F3F7F5',
		hover: '#E8EEEB',
		secondary: '#3F4A45',
		shadowColor: '16 24 20',
	},
	dark: {
		page: '#0F1412',
		card: '#1A211E',
		sidebar: '#0A0D0C',
		ink: '#E8EDEA',
		muted: '#A3ADA8',
		border: '#2A322E',
		input: '#36403B',
		primary: '#34D399',
		primaryHover: '#6EE7B7',
		onPrimary: '#06281C',
		activeBg: '#123326',
		activeFg: '#6EE7B7',
		gridHeader: '#151B18',
		hover: '#202925',
		secondary: '#B5BFBA',
		shadowColor: '0 0 0',
	},
} as const

// The logo strip stays dark in both modes: white-label logos are often
// light-on-dark.
export const LOGO_STRIP = { background: '#0F1412', border: '#1F2824' }

export const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

// Motion effects sit behind this query so reduced-motion users skip them.
const MOTION_OK = '@media (prefers-reduced-motion: no-preference)'

const DISPLAY_FONT = '"Space Grotesk", "Inter Variable", system-ui, sans-serif'

type SidebarPalette = {
	background: string
	activeBg: string
	activeFg: string
}

declare module '@mui/material/styles' {
	interface Palette {
		sidebar: SidebarPalette
	}
	interface PaletteOptions {
		sidebar?: SidebarPalette
	}
}

export function getThemeOptions(mode: PaletteMode): ThemeOptions {
	const t = tokens[mode]
	const shadow = (a: number) => `rgb(${t.shadowColor} / ${a}%)`
	const cardShadow = `0 1px 2px ${shadow(6)}, 0 1px 3px -1px ${shadow(10)}`
	const popoverShadow = `0 8px 24px -8px ${shadow(28)}, 0 2px 6px -2px ${shadow(16)}`
	const focusGlow = `0 0 0 3px ${alpha(t.primary, 0.18)}`
	const caps = {
		fontSize: 12,
		fontWeight: 600,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
		color: t.muted,
	} as const

	return {
		palette: {
			mode,
			primary: {
				main: t.primary,
				dark: t.primaryHover,
				contrastText: t.onPrimary,
			},
			// Pages use `secondary` for neutral icons and panel titles.
			secondary: { main: t.secondary, light: t.ink, contrastText: t.card },
			background: { default: t.page, paper: t.card },
			text: { primary: t.ink, secondary: t.muted },
			divider: t.border,
			action: { hover: t.hover },
			sidebar: {
				background: t.sidebar,
				activeBg: t.activeBg,
				activeFg: t.activeFg,
			},
		},
		shape: { borderRadius: 8 },
		// MUI Grow, Fade, Slide and Collapse read these.
		transitions: {
			easing: { easeOut: EASE },
			duration: { enteringScreen: 200, leavingScreen: 150 },
		},
		typography: {
			fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
			fontSize: 14,
			h1: { fontFamily: DISPLAY_FONT, fontWeight: 600 },
			h2: { fontFamily: DISPLAY_FONT, fontWeight: 600 },
			h3: { fontFamily: DISPLAY_FONT, fontWeight: 600 },
			h4: {
				fontFamily: DISPLAY_FONT,
				fontWeight: 600,
				fontSize: 26,
				lineHeight: '32px',
				letterSpacing: '-0.015em',
			},
			h5: { fontWeight: 600, fontSize: 20, letterSpacing: '-0.01em' },
			h6: { fontWeight: 600, fontSize: 17 },
			button: { textTransform: 'none', fontWeight: 500 },
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						letterSpacing: '-0.006em',
						fontVariantNumeric: 'tabular-nums',
						WebkitFontSmoothing: 'antialiased',
					},
					'*::-webkit-scrollbar': { width: 10, height: 10 },
					'*::-webkit-scrollbar-thumb': {
						backgroundColor: alpha(t.ink, 0.16),
						borderRadius: 10,
						border: '2px solid transparent',
						backgroundClip: 'padding-box',
					},
					'@keyframes ply-dialog-in': {
						from: { opacity: 0, transform: 'scale(0.96) translateY(8px)' },
						to: { opacity: 1, transform: 'none' },
					},
				},
			},
			MuiButton: {
				defaultProps: { disableElevation: true },
				styleOverrides: {
					root: {
						borderRadius: 8,
						minHeight: 36,
						paddingInline: 14,
						fontWeight: 500,
						[MOTION_OK]: {
							transition: `background-color 150ms, box-shadow 150ms, border-color 150ms, color 150ms`,
							// Scale the icons, not the button. Scaling the root moves its
							// edge out from under the pointer and the click is lost.
							'& .MuiButton-startIcon, & .MuiButton-endIcon': {
								transition: `transform 120ms ${EASE}`,
							},
							'&:active .MuiButton-startIcon, &:active .MuiButton-endIcon': {
								transform: 'scale(0.85)',
							},
						},
					},
					sizeSmall: { minHeight: 32, paddingInline: 10 },
				},
				variants: [
					{
						props: { variant: 'contained', color: 'primary' },
						style: { '&:hover': { backgroundColor: t.primaryHover } },
					},
					// Toolbar and secondary actions read as quiet ink buttons.
					{
						props: { variant: 'text', color: 'primary' },
						style: {
							color: t.ink,
							'&:hover': { backgroundColor: t.hover },
						},
					},
					{
						props: { variant: 'outlined', color: 'primary' },
						style: {
							borderColor: t.input,
							backgroundColor: t.card,
							color: t.ink,
							'&:hover': { borderColor: t.muted, backgroundColor: t.hover },
						},
					},
				],
			},
			// Shared ply-ux ToggleButtons tint the selected option with
			// `secondary`. Give it the active-nav look instead. The extra class
			// in the selector beats the component's own sx.
			MuiButtonBase: {
				styleOverrides: {
					root: {
						'&.selected.secondary.MuiButtonBase-root, &.selected.primary.MuiButtonBase-root':
							{
								borderColor: t.primary,
								backgroundColor: t.activeBg,
								color: t.activeFg,
							},
					},
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: {
						borderRadius: 8,
						[MOTION_OK]: {
							transition: `background-color 150ms`,
							'& .MuiSvgIcon-root': { transition: `transform 120ms ${EASE}` },
							'&:active .MuiSvgIcon-root': { transform: 'scale(0.85)' },
						},
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: { backgroundImage: 'none' },
					rounded: { borderRadius: 12 },
					outlined: { borderColor: t.border },
				},
				variants: [
					{
						props: { elevation: 1 },
						style: {
							'--Paper-overlay': 'none',
							border: `1px solid ${t.border}`,
							boxShadow: cardShadow,
						},
					},
				],
			},
			MuiCard: {
				defaultProps: { variant: 'outlined' },
				styleOverrides: {
					root: {
						borderRadius: 12,
						boxShadow: cardShadow,
						// Only clickable cards lift; form cards stay still.
						[`${MOTION_OK} and (hover: hover)`]: {
							transition: `transform 200ms ${EASE}, box-shadow 200ms ${EASE}`,
							'&:has(> .MuiCardActionArea-root):hover': {
								transform: 'translateY(-2px)',
								boxShadow: popoverShadow,
							},
						},
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						[MOTION_OK]: { animation: `ply-dialog-in 220ms ${EASE}` },
					},
				},
			},
			MuiPopover: {
				styleOverrides: {
					paper: {
						borderRadius: 8,
						border: `1px solid ${t.border}`,
						boxShadow: popoverShadow,
					},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						backgroundColor: mode === 'light' ? t.ink : '#2C3531',
						color: '#FFFFFF',
						fontSize: 12,
						fontWeight: 500,
						borderRadius: 6,
					},
				},
			},
			MuiTextField: {
				defaultProps: {
					size: 'small',
					variant: 'outlined',
					margin: 'normal',
				},
			},
			MuiPickersTextField: {
				defaultProps: { variant: 'outlined', size: 'small' },
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						borderRadius: 8,
						backgroundColor: t.card,
						transition: `box-shadow 150ms ${EASE}`,
						'& .MuiOutlinedInput-notchedOutline': { borderColor: t.input },
						'&:hover .MuiOutlinedInput-notchedOutline': {
							borderColor: t.muted,
						},
						'&.MuiInputBase-sizeSmall .MuiOutlinedInput-input': {
							paddingTop: 6.5,
							paddingBottom: 6.5,
						},
						'&.Mui-focused': { boxShadow: focusGlow },
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							borderColor: t.primary,
							borderWidth: 1,
						},
					},
				},
			},
			MuiPickersOutlinedInput: {
				styleOverrides: {
					root: {
						borderRadius: 8,
						backgroundColor: t.card,
						'& .MuiPickersOutlinedInput-notchedOutline': {
							borderColor: t.input,
						},
						'&.Mui-focused': { boxShadow: focusGlow },
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: { root: { color: t.muted } },
			},
			MuiTabs: {
				styleOverrides: {
					root: {
						minHeight: 40,
						// Entity pages: a side list styled like the sidebar nav. The
						// nested selector beats the shared tabs' hardcoded white.
						'&.MuiTabs-vertical': {
							backgroundColor: 'transparent',
							borderRight: 0,
							paddingLeft: 12,
							paddingRight: 12,
							'& .MuiTabs-indicator': { display: 'none' },
							'& .MuiTabs-list': { gap: 2 },
							'& .MuiTab-root': {
								minHeight: 36,
								borderRadius: 8,
								padding: '8px 12px',
								alignItems: 'flex-start',
								justifyContent: 'center',
								textAlign: 'left',
								color: t.muted,
								fontWeight: 500,
								transition: `background-color 150ms ${EASE}`,
								'&:hover': { backgroundColor: t.hover, color: t.ink },
								'&.Mui-selected': {
									backgroundColor: t.activeBg,
									color: t.activeFg,
								},
							},
						},
					},
					indicator: {
						height: 2,
						borderRadius: 2,
						[MOTION_OK]: { transition: `all 250ms ${EASE}` },
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						textTransform: 'none',
						fontWeight: 500,
						minHeight: 40,
						padding: '8px 12px',
						color: t.muted,
						'&.Mui-selected': { color: t.activeFg },
					},
				},
			},
			MuiTableSortLabel: {
				defaultProps: { hideSortIcon: true },
			},
			MuiTableCell: {
				styleOverrides: {
					root: { borderColor: t.border },
					head: { ...caps, backgroundColor: t.gridHeader },
				},
			},
			MuiTableRow: {
				styleOverrides: {
					hover: { '&:hover': { backgroundColor: t.hover } },
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					root: {
						borderColor: t.border,
						borderRadius: 12,
						'--DataGrid-rowBorderColor': t.border,
						'--DataGrid-containerBackground': t.gridHeader,
					},
					columnHeaderTitle: caps,
					row: { '&:hover': { backgroundColor: t.hover } },
				},
			},
			MuiChip: {
				styleOverrides: {
					root: { borderRadius: 6, fontSize: 12, fontWeight: 500 },
					outlined: { borderColor: t.border },
				},
			},
			MuiToggleButton: {
				styleOverrides: {
					root: {
						textTransform: 'none',
						fontWeight: 500,
						borderColor: t.input,
						color: t.muted,
						'&.Mui-selected, &.Mui-selected:hover': {
							color: t.activeFg,
							backgroundColor: t.activeBg,
						},
					},
				},
			},
			MuiCheckbox: {
				defaultProps: { color: 'primary' },
				styleOverrides: {
					root: {
						[MOTION_OK]: {
							'& .MuiSvgIcon-root': { transition: `transform 150ms ${EASE}` },
							'&:active .MuiSvgIcon-root': { transform: 'scale(0.85)' },
						},
					},
				},
			},
			MuiRadio: {
				defaultProps: { color: 'primary' },
			},
			MuiSwitch: {
				defaultProps: { color: 'primary' },
				styleOverrides: {
					switchBase: {
						[MOTION_OK]: {
							transitionTimingFunction: EASE,
							transitionDuration: '250ms',
						},
					},
				},
			},
			MuiDivider: {
				styleOverrides: { root: { borderColor: t.border } },
			},
		},
	}
}
